<template>
  <the-preloader />
  <particles-background />
  <div class="header" :style="{ 'background-image': 'url(' + content.header.background + ')' }">
    <the-header :headerText="content.header" />
  </div>
  <div v-if="content.header.generateFullPage">
    <the-menu :headerText="content.header" :activeBlocks="content.visibility"></the-menu>
    <div class="introduction">
      <div v-if="content.visibility.firstBlock" id="intro" class="component-section" v-reveal>
        <the-introduction :intro="content.introduction"></the-introduction>
      </div>
      <div v-if="content.visibility.sideProgram" id="side-program" class="component-section" v-reveal>
        <side-program :data="content.sideProgram" :config="content.config"></side-program>
      </div>
      <div v-if="content.visibility.speakers" id="speakers" class="component-section" v-reveal>
        <speakers-page :userList="content.speakers" :config="content.config"></speakers-page>
      </div>
      <div v-if="content.visibility.videoGallery" id="video-gallery" class="component-section" v-reveal>
        <the-video-gallery :data="content.videoGallery"></the-video-gallery>
      </div>
      <div v-if="content.visibility.program" id="program" class="component-section" v-reveal>
        <conference-schedule :program="content.programs"></conference-schedule>
      </div>
      <div v-if="content.visibility.numbers" id="numbers" class="component-section" v-reveal>
        <the-numbers :numbers="content.introduction"></the-numbers>
      </div>
    </div>
    <div v-if="content.visibility.registration" id="entrance" class="component-section" v-reveal>
      <entrance-fee :fee="content.entrance"></entrance-fee>
    </div>
    <div v-if="content.visibility.trainings" id="trainings" class="component-section" v-reveal>
      <the-trainings :data="content.trainings"></the-trainings>
    </div>
    <div v-if="content.visibility.faq" id="faq" class="component-section" v-reveal>
      <the-faq :faqData="content.faq"></the-faq>
    </div>
    <div v-if="content.visibility.feedback" id="feedback" class="component-section" v-reveal>
      <the-feedback :ratings="content.feedback"></the-feedback>
    </div>
    <div v-if="content.visibility.register" id="register" class="component-section" v-reveal>
      <the-registration :register="content.registration"></the-registration>
    </div>
    <div v-if="content.visibility.gallery" id="photogallery" class="component-section" v-reveal>
      <the-photogallery :gallery="content.photogallery"></the-photogallery>
    </div>
    <div v-if="content.visibility.partners" id="partner" v-reveal>
      <the-partners :partners="content.partners"></the-partners>
    </div>
    <div v-if="content.visibility.contacts" id="contact" v-reveal>
      <contact-address :contact="content.footer"></contact-address>
    </div>
    <div id="footer">
      <the-footer-bar :link="content.header"></the-footer-bar>
    </div>
  </div>
  <div id="cookie-banner">
    <cookie-banner></cookie-banner>
  </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import { localize } from "@/i18n";

// STATICKÉ KOMPONENTY
import ThePreloader from "./components/ThePreloader.vue";
import ParticlesBackground from "./components/ParticlesBackground.vue";
import TheHeader from "@/components/TheHeader.vue";
import TheMenu from "./components/TheMenu.vue";
import TheIntroduction from "./components/TheIntroduction.vue";

// ASYNCHRONNÍ KOMPONENTY
const SpeakersPage = defineAsyncComponent(() =>
  import("./components/SpeakersPage.vue"),
);
const ConferenceSchedule = defineAsyncComponent(() =>
  import("./components/ConferenceSchedule.vue"),
);
const TheNumbers = defineAsyncComponent(() =>
  import("./components/TheNumbers.vue"),
);
const EntranceFee = defineAsyncComponent(() =>
  import("./components/EntranceFee.vue"),
);
const TheFaq = defineAsyncComponent(() => import("./components/TheFaq.vue"));
const TheFeedback = defineAsyncComponent(() =>
  import("./components/TheFeedback.vue"),
);
const TheRegistration = defineAsyncComponent(() =>
  import("./components/TheRegistration.vue"),
);
const ThePhotogallery = defineAsyncComponent(() =>
  import("./components/ThePhotogallery.vue"),
);
const ThePartners = defineAsyncComponent(() =>
  import("./components/ThePartners.vue"),
);
const ContactAddress = defineAsyncComponent(() =>
  import("./components/ContactAddress.vue"),
);
const TheFooterBar = defineAsyncComponent(() =>
  import("./components/TheFooterBar.vue"),
);
const CookieBanner = defineAsyncComponent(() =>
  import("./components/CookieBanner.vue"),
);
const TheTrainings = defineAsyncComponent(() =>
  import("./components/TheTrainings.vue"),
);
const SideProgram = defineAsyncComponent(() =>
  import("./components/SideProgram.vue"),
);
const TheVideoGallery = defineAsyncComponent(() =>
  import("./components/TheVideoGallery.vue")
);

export default {
  name: "App",
  props: {
    // Obsah zobrazeného ročníku (aktuální nebo archivní), viz src/editions.
    source: {
      type: Object,
      required: true,
    },
  },
  computed: {
    content() {
      return localize(this.source, this.$locale);
    },
  },
  components: {
    // Statické
    ThePreloader,
    ParticlesBackground,
    TheHeader,
    TheMenu,
    TheIntroduction,

    // Asynchronní
    SpeakersPage,
    ConferenceSchedule,
    TheNumbers,
    EntranceFee,
    TheFaq,
    TheFeedback,
    ThePhotogallery,
    ThePartners,
    TheRegistration,
    ContactAddress,
    TheFooterBar,
    CookieBanner,
    SideProgram,
    TheTrainings,
    TheVideoGallery
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap");

:root {
  --bg-primary: #0b0f19;
  --bg-secondary: #121826;
  --bg-panel: rgba(18, 24, 38, 0.96);

  --text-primary: #f0f0f0;
  --text-secondary: #94a3b8;

  --accent-primary: #ff9800;
  --accent-neon: #00f0ff;
  --accent-danger: #ff003c;

  --glass-border: rgba(255, 255, 255, 0.1);
  --glass-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);

  --font-main: "Inter", sans-serif;
  --font-mono: "JetBrains Mono", monospace;
}

* {
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  margin: 0;
  -webkit-tap-highlight-color: transparent;
}

html {
  background-color: #0b0f19 !important;
  margin: 0;
  padding: 0;
  width: 100%;
}

body {
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  background-color: var(--bg-primary);
  -webkit-text-size-adjust: 100%;
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 10px;
}

#footer {
  position: relative;
  z-index: 10;
}

::-webkit-scrollbar-track {
  background: var(--bg-primary);
}

::-webkit-scrollbar-thumb {
  background: var(--accent-primary);
  border-radius: 5px;
  border: 2px solid var(--bg-primary);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--accent-neon);
}

h1,
h2,
h3,
h4,
h5,
h6 {
  font-weight: 700;
  color: var(--text-primary);
}

#numbers {
  margin-top: 200px;
  margin-bottom: 30px;
}

div.header {
  height: 100vh !important;
  background-attachment: fixed;
  background-position: center;
  background-size: cover;
  position: relative;
}

div.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom,
      rgba(11, 15, 25, 0.3),
      var(--bg-primary));
  pointer-events: none;
}

div.introduction {
  background-color: var(--bg-primary);
  background-size: cover;
  margin-top: 90px;
}

#intro {
  margin-top: 35px;
}

.component-section {
  margin-bottom: 100px;
  min-height: 200px;
  contain: content;
}

/* Neon accents for specific texts */
.second-color {
  color: var(--accent-primary);
  text-shadow: 0 0 10px rgba(255, 152, 0, 0.3);
}

.block-justify {
  text-align: justify !important;
}

a {
  color: var(--accent-primary);
  text-decoration: none;
  transition: color 0.3s ease, text-shadow 0.3s ease;
}

a:hover {
  color: var(--accent-neon);
  text-shadow: 0 0 8px rgba(0, 240, 255, 0.5);
}

#speakers,
#program,
#ctf,
#photogallery,
#feedback,
#partner,
#register,
#entrance {
  scroll-margin-top: 90px;
}

.title-icon {
  width: 75px;
  height: 75px;
}

.glass-panel {
  background: var(--bg-panel);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--glass-border);
  box-shadow: var(--glass-shadow);
  border-radius: 12px;
}

/* Scroll Reveal Animations */
.reveal-on-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  will-change: opacity, transform;
}

.reveal-on-scroll.is-revealed {
  opacity: 1;
  transform: translateY(0);
}

@media only screen and (max-width: 480px) {
  .reveal-on-scroll {
    opacity: 1 !important;
    transform: none !important;
    transition: opacity 0.4s ease-in !important;
  }
}

/* Glitch Text Effect */
.glitch-text {
  position: relative;
  display: inline-block;
}

.glitch-text::before,
.glitch-text::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--bg-primary);
}

.glitch-text::before {
  left: 2px;
  text-shadow: -2px 0 var(--accent-neon);
  opacity: 0;
  animation: glitch-anim-1 6s infinite linear alternate-reverse;
}

.glitch-text::after {
  left: -2px;
  text-shadow: -2px 0 var(--accent-primary);
  opacity: 0;
  animation: glitch-anim-2 6s infinite linear alternate-reverse;
}

@keyframes glitch-anim-1 {
  0% {
    clip-path: inset(20% 0 80% 0);
    transform: translate(-2px, 1px);
    opacity: 1;
  }

  2% {
    clip-path: inset(60% 0 10% 0);
    transform: translate(2px, -1px);
    opacity: 1;
  }

  4% {
    clip-path: inset(40% 0 50% 0);
    transform: translate(-2px, 2px);
    opacity: 1;
  }

  6% {
    clip-path: inset(80% 0 5% 0);
    transform: translate(2px, -2px);
    opacity: 1;
  }

  8% {
    clip-path: inset(10% 0 70% 0);
    transform: translate(-1px, 1px);
    opacity: 1;
  }

  10% {
    clip-path: inset(30% 0 50% 0);
    transform: translate(1px, -1px);
    opacity: 1;
  }

  11% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@keyframes glitch-anim-2 {
  0% {
    opacity: 0;
  }

  60% {
    opacity: 0;
  }

  61% {
    clip-path: inset(10% 0 60% 0);
    transform: translate(2px, -1px);
    opacity: 1;
  }

  63% {
    clip-path: inset(30% 0 20% 0);
    transform: translate(-2px, 2px);
    opacity: 1;
  }

  65% {
    clip-path: inset(70% 0 10% 0);
    transform: translate(2px, -2px);
    opacity: 1;
  }

  67% {
    clip-path: inset(20% 0 50% 0);
    transform: translate(-1px, 1px);
    opacity: 1;
  }

  69% {
    clip-path: inset(50% 0 30% 0);
    transform: translate(1px, -1px);
    opacity: 1;
  }

  71% {
    clip-path: inset(5% 0 80% 0);
    transform: translate(-2px, 1px);
    opacity: 1;
  }

  72% {
    opacity: 0;
  }

  100% {
    opacity: 0;
  }
}

@media only screen and (max-width: 900px) {
  div.header {
    background-attachment: inherit;
  }
}
</style>
